<template>
    <modal-side-generic
    invertTitleSubtitle
	:title="isRegLogin ? t('login.acessionsModal.fromRegLoginTitle') : t('login.acessionsModal.fromAcessionLoginTitle')"
	:subTitle="getRememberMeEntityInfo && getRememberMeEntityInfo.entityName"
    :showFooter="getRememberMeEntityInfo && getRememberMeEntityInfo.listOfAcessions.length"
    :buttonsFooterData="footerButton"
    :triggerClose="triggerClose"
	@hideMe="emit('hideMe')">
        <!-- Empty-state -->
		<empty-state-generic
		v-if="!getRememberMeEntityInfo || !getRememberMeEntityInfo.listOfAcessions.length"
		illustration="emptyState/acessions"
		:title="t('login.acessionsModal.emptyStateTitle')"
		:description="t('login.acessionsModal.emptyStateDescription')"/>
        <!-- *********************************************
        *            Memorized acessions wrapper         *
        ********************************************** -->
        <!-- sfdsf -->
        <div v-else class="list-of-acessions-wrapper prevent-auto-margins" :class="{ 'mobile': getMobileAppState }">
            <!-- Particular -->
            <div
            v-if="getClientType === 'particular' && getRememberMeEntityInfo.listOfAcessions.find(el => el.type === 'particular')"
            style="margin-bottom: 32px"
            class="block">
                <divider-generic
                icon="person"
                :title="t('login.acessionsModal.particularBlock')"
                :applyBorder="false"/>
                <!-- Table -->
                <tables-generic
                hideHeader
                hideDetailButton
                :headerData="tableHeaderData"
                :tableStyles="tableStyles"
                :tableData="particularTableData"
                @moreOptionsClicked="moreOptionsClickHandler"
                @rowClicked="rowClickHandler"/>
            </div>
            <!-- Company -->
            <div
            v-if="getClientType === 'company' && getRememberMeEntityInfo.listOfAcessions.find(el => el.type === 'company')"
            class="block">
                <divider-generic
                icon="building"
                dividerSize="small"
                :title="t('login.acessionsModal.companyBlock')"
                :applyBorder="false"/>
                <!-- Table -->
                <tables-generic
                hideHeader
                hideDetailButton
                :headerData="tableHeaderData"
                :tableStyles="tableStyles"
                :tableData="companyTableData"
                @moreOptionsClicked="moreOptionsClickHandler"
                @rowClicked="rowClickHandler"/>
            </div>
        </div>
	</modal-side-generic>
    <!-- *********************************************
    *              Remove Acession Modal             *
    ********************************************** -->
    <modal-centered-generic
    v-if="showRemoveAcessionModal"
    negative
    titleIcon="delete"
    rightButtonIcon="check-line"
    :title="t('login.removeAcessionModal.title')"
    :text="t('login.removeAcessionModal.text', {contractName: selectedAcession.contractName || selectedAcession.acessionNumber})"
    :leftButtonText="t('login.removeAcessionModal.leftButtonText')"
    :rightButtonText="t('login.removeAcessionModal.rightButtonText')"
    @hideMe="showRemoveAcessionModal = false"
    @rightButtonClicked="removeAcessionInfo"/>

    <!-- Feedback toast -->
    <toast-generic
    v-if="showFeedbackToast"
    toastType="success"
    :feedbackText="t('login.successFeedbackToast')"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TablesGeneric from '@/components/generic/table/Generic'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
/* vue */
import { computed, ref } from 'vue'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'goToRegLogin', 'updateRegLogin', 'goToRememberMeLogin'])

/* mapActions */
const { removeAcessionFromEntityRememberMeInfo, updateSelectedEntityRememberInfo } = mapActions(moduleMapper.SESSION)
const { updateAppThemeClientType } = mapActions()
const { getRememberMeEntityInfo } = mapGetters(moduleMapper.SESSION)
const { getMobileAppState, getClientType } = mapGetters()

/** Props received:
@isRegLogin { Boolean }
@triggerClose { Boolean }
*/

const props = defineProps({
    isRegLogin: Boolean,
    triggerClose: Boolean
})

const footerButton = [{
    buttonType: 'primary',
    label: t('login.acessionsModal.footerBtn'),
    icon: 'login',
    iconPosition: 'right',
    callbackFunction: () => emit('goToRegLogin')
}]

const tableHeaderData = [
    { label: 'title', dataAttr: 'contractName', rowCellType: 'title', showInMobile: true },
    { dataAttr: 'tableOptions', showInMobile: true, rowCellType: 'more-options' }
]

const tableStyles = [null, null]

const particularTableData = computed(() => {
    return getRememberMeEntityInfo.value.listOfAcessions.filter(ele => ele.type === 'particular').map(acession => {
        return [
            { value: acession.acessionNumber, dataAttr: 'id' },
            { type: 'title', value: acession.contractName || maskedAccessionNumber(acession.acessionNumber), dataAttr: 'contractName' },
            { type: 'more-options', value: moreOptionsOptions, dataAttr: 'tableOptions' }
        ]
    })
})

const companyTableData = computed(() => {
    return getRememberMeEntityInfo.value.listOfAcessions.filter(ele => ele.type === 'company').map(acession => {
        return [
            { value: acession.acessionNumber, dataAttr: 'id' },
            { type: 'title', value: acession.contractName || maskedAccessionNumber(acession.acessionNumber), dataAttr: 'contractName' },
            { type: 'more-options', value: moreOptionsOptions, dataAttr: 'tableOptions' }
        ]
    })
})

const maskedAccessionNumber = (accessionNumber) => {
    const lastDigits = accessionNumber.slice(accessionNumber.length - 2, accessionNumber.length)

    return lastDigits.toString().padStart(accessionNumber.length, '*')
}

const showRemoveAcessionModal = ref(false)
const selectedAcession = ref({})
const moreOptionsClickHandler = $event => {
    selectedAcession.value = getRememberMeEntityInfo.value.listOfAcessions.find(el => el.acessionNumber === $event.rowId)

    showRemoveAcessionModal.value = true
}

const showFeedbackToast = ref(false)
const removeAcessionInfo = () => {
    removeAcessionFromEntityRememberMeInfo(selectedAcession.value.acessionNumber)
    showFeedbackToast.value = true
    showRemoveAcessionModal.value = false

    if (getRememberMeEntityInfo.value && !getRememberMeEntityInfo.value.listOfAcessions.find(el => el.selected)) {
        updateSelectedEntityRememberInfo(getRememberMeEntityInfo.value.listOfAcessions[0].acessionNumber)
        updateAppThemeClientType(getRememberMeEntityInfo.value.listOfAcessions.find(el => el.selected).type.toLowerCase())
    }

    if (!getRememberMeEntityInfo.value) emit('updateRegLogin')
}

const moreOptionsOptions = [
	{
		id: 1,
		text: t('login.acessionsModal.moreOptions'),
		icon: 'delete',
        variant: 'negative'
	}
]

const rowClickHandler = row => {
    updateSelectedEntityRememberInfo(row.rowData[0].value)
    updateAppThemeClientType(getRememberMeEntityInfo.value.listOfAcessions.find(el => el.selected).type.toLowerCase())

    emit('goToRememberMeLogin')
}
</script>

<style lang="scss" scoped>
.empty-state-wrapper {
    max-width: 356px;
    align-self: center;
    margin-top: 72px;
}

.list-of-acessions-wrapper {
    .block {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &.mobile {
        .block {
            :deep(.divider-generic-wrapper) {
                padding: 0 16px;
            }
        }
    }
}
</style>
